/**
 * Garage: the customer's current and previously selected machines.
 *
 * localStorage is the source of truth for guests. When the droplet's App Proxy
 * endpoint exists (docs/fitment-api-spec.md §4.3), signed-in customers sync
 * through `syncEndpoint` — but the local copy stays authoritative for rendering
 * so the UI never blocks on the network.
 *
 * Emits `garage:changed` on document for any mutation. Also listens to the
 * native `storage` event, so a change in one tab lands in the others.
 */
(function () {
  'use strict';

  var STORE_KEY = 'fmt:garage';
  var MAX_VEHICLES = 20;

  var state = { active: null, vehicles: [] };
  var syncEndpoint = null;
  var customerId = null;

  function read() {
    try {
      var raw = localStorage.getItem(STORE_KEY);
      if (!raw) return { active: null, vehicles: [] };
      var parsed = JSON.parse(raw);
      return {
        active: typeof parsed.active === 'string' ? parsed.active : null,
        vehicles: Array.isArray(parsed.vehicles) ? parsed.vehicles : []
      };
    } catch (e) {
      return { active: null, vehicles: [] };
    }
  }

  function write() {
    try {
      localStorage.setItem(STORE_KEY, JSON.stringify(state));
    } catch (e) {
      /* private mode or quota — the session still works, it just won't persist */
    }
  }

  /**
   * `change` describes what just happened ({ type, key, existed }), so a
   * panel can say "already in your garage" rather than silently reordering.
   */
  function emit(change) {
    document.dispatchEvent(new CustomEvent('garage:changed', {
      detail: { active: active(), vehicles: state.vehicles.slice(), change: change || null }
    }));
  }

  function commit(change) {
    write();
    emit(change);
    push();
  }

  /**
   * Upstream sync. Never blocks or reverts local state, but never pretends
   * either: the outcome is announced as `garage:sync` so the UI can say the
   * garage is saved on this device but not (yet) to the account.
   */
  function push() {
    if (!syncEndpoint || !customerId) return;
    fetch(syncEndpoint, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        active_key: state.active,
        vehicles: state.vehicles.map(function (v) {
          return { key: v.key, nickname: v.nickname || null };
        })
      })
    })
      .then(function (r) { announceSync(r.ok, r.ok ? null : 'The server answered ' + r.status + '.'); })
      ['catch'](function () { announceSync(false, 'You appear to be offline.'); });
  }

  function announceSync(ok, reason) {
    document.dispatchEvent(new CustomEvent('garage:sync', { detail: { ok: ok, reason: reason || null } }));
  }

  function indexOf(key) {
    for (var i = 0; i < state.vehicles.length; i++) {
      if (state.vehicles[i].key === key) return i;
    }
    return -1;
  }

  function active() {
    if (!state.active) return null;
    var i = indexOf(state.active);
    return i === -1 ? null : state.vehicles[i];
  }

  /**
   * Add a vehicle and make it active. Re-adding an existing vehicle moves it to
   * the front and keeps its nickname rather than duplicating it.
   */
  function add(vehicle) {
    if (!vehicle || !vehicle.key) return null;

    var existing = indexOf(vehicle.key);
    var entry;

    if (existing !== -1) {
      entry = state.vehicles.splice(existing, 1)[0];
      // Refresh derived fields in case the taxonomy renamed the model.
      entry.label = vehicle.label || entry.label;
      entry.type = vehicle.type || entry.type;
    } else {
      entry = {
        key: vehicle.key,
        label: vehicle.label || vehicle.key,
        year: vehicle.year || null,
        make: vehicle.make || null,
        model: vehicle.model || null,
        type: vehicle.type || null,
        nickname: null,
        added_at: new Date().toISOString()
      };
    }

    state.vehicles.unshift(entry);
    if (state.vehicles.length > MAX_VEHICLES) {
      state.vehicles.length = MAX_VEHICLES;
    }
    state.active = entry.key;

    commit({ type: 'add', key: entry.key, existed: existing !== -1 });
    return entry;
  }

  function remove(key) {
    var i = indexOf(key);
    if (i === -1) return false;

    state.vehicles.splice(i, 1);
    // Fall back to the next most recent rather than leaving nothing selected.
    if (state.active === key) {
      state.active = state.vehicles.length ? state.vehicles[0].key : null;
    }

    commit({ type: 'remove', key: key });
    return true;
  }

  function setActive(key) {
    if (key !== null && indexOf(key) === -1) return false;
    state.active = key;
    commit({ type: 'activate', key: key });
    return true;
  }

  function rename(key, nickname) {
    var i = indexOf(key);
    if (i === -1) return false;
    var clean = nickname ? String(nickname).trim().slice(0, 64) : '';
    // A nickname equal to the real name is no nickname.
    if (clean === state.vehicles[i].label) clean = '';
    state.vehicles[i].nickname = clean || null;
    commit({ type: 'rename', key: key });
    return true;
  }

  function clear() {
    state = { active: null, vehicles: [] };
    commit();
  }

  /**
   * Merge the server's garage into the local one on login. Union by key; the
   * older `added_at` wins so a vehicle keeps its original add date. The server
   * only ever holds keys we can validate, so unknown ones are dropped by it.
   */
  function mergeRemote(remote) {
    if (!remote || !Array.isArray(remote.vehicles)) return;

    remote.vehicles.forEach(function (rv) {
      if (!rv || !rv.key) return;
      var i = indexOf(rv.key);
      if (i === -1) {
        state.vehicles.push({
          key: rv.key,
          label: rv.label || rv.key,
          year: rv.year || null,
          make: rv.make || null,
          model: rv.model || null,
          type: rv.type || null,
          nickname: rv.nickname || null,
          added_at: rv.added_at || new Date().toISOString()
        });
      } else if (rv.added_at && rv.added_at < state.vehicles[i].added_at) {
        state.vehicles[i].added_at = rv.added_at;
        state.vehicles[i].nickname = state.vehicles[i].nickname || rv.nickname || null;
      }
    });

    state.vehicles.sort(function (a, b) {
      return String(b.added_at).localeCompare(String(a.added_at));
    });
    if (state.vehicles.length > MAX_VEHICLES) state.vehicles.length = MAX_VEHICLES;

    if (!state.active && remote.active_key && indexOf(remote.active_key) !== -1) {
      state.active = remote.active_key;
    }

    commit();
  }

  /**
   * @param {object} opts
   * @param {string} [opts.syncEndpoint] App Proxy path, e.g. /apps/fitment/garage
   * @param {string|number} [opts.customerId] Shopify customer ID, falsy for guests
   */
  function init(opts) {
    opts = opts || {};
    syncEndpoint = opts.syncEndpoint || null;
    customerId = opts.customerId || null;

    state = read();

    if (syncEndpoint && customerId) {
      fetch(syncEndpoint, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (remote) { if (remote) mergeRemote(remote); })
        ['catch'](function () { /* local copy stands */ });
    }

    emit();
  }

  window.addEventListener('storage', function (e) {
    if (e.key !== STORE_KEY) return;
    state = read();
    emit();
  });

  window.Garage = {
    init: init,
    add: add,
    remove: remove,
    setActive: setActive,
    rename: rename,
    clear: clear,
    active: active,
    get vehicles() { return state.vehicles.slice(); },
    get count() { return state.vehicles.length; },
    MAX_VEHICLES: MAX_VEHICLES
  };
})();
